import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { get } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

export interface AssignedAccount {
  customer_id: string;
  customer_name: string;
  industry: string | null;
  tier: string;
  role_type: string;
  region: string;
  location: string | null;
  customer_level: string | null;
  alignment_level: string | null;
  revenue: number | null;
  renewal_date: string | null;
  primary_sponsors: string | null;
  primary_sponsors_id: string | null;
  secondary_sponsors: string | null;
  secondary_sponsors_id: string | null;
}

export interface AssignedAccountsResponse {
  assigned_as_primary: number;
  assigned_as_secondary: number;
  assigned_as_executive?: number;
  total_revenue: number;
  total: number;
  accounts: AssignedAccount[];
}

export interface FetchAssignedAccountsParams {
  tier?: string | null;
  region?: string | null;
  customer_level?: string | null;
  /** Single role or comma-separated, e.g. "primary" or "primary,secondary" */
  role?: string | null;
  sort_by?: string | null;
  sort_direction?: "asc" | "desc" | null;
}

export interface PortfolioRosterPerson {
  user_id: string;
  name: string;
  status?: "completed" | "pending" | string;
  role?: "sponsor" | "account_team" | "other_executive" | string;
}

export interface PortfolioAccount {
  account_id: string;
  account_name: string;
  sector: string | null;
  current_level: string | null;
  last_assessment: string | null;
  /** "Your status" — this user's own submit-based freshness. */
  assessment_status: string | null;
  /** "Account status" — publish-driven freshness, independent of this user's submissions. */
  org_assessment_status?: string | null;
  published_at?: string | null;
  published_level?: string | null;
  published_origin?: "formal" | "self" | null;
  your_assessment_level?: string | null;
  your_assessment_date?: string | null;
  your_assessment_origin?: "formal" | "self" | null;
  pending_formal_count?: number;
  cycle_due_at?: string | null;
  submit_locked?: boolean;
  pending_participants?: PortfolioRosterPerson[];
  /** Running cycle progress: assignees who responded / all assignees. */
  pending_responded_count?: number;
  pending_participant_count?: number;
  /** ISO date this user's private remainder input counts on. */
  your_assessment_pending_counts_on?: string | null;
  /** Latest input is held by an open disconnect; counts after it. */
  your_assessment_held?: boolean;
  onboarded_at?: string | null;
  your_published_level?: string | null;
  your_published_date?: string | null;
  status_phase?: "pending" | "completed" | "none" | null;
  status_kind?: "formal" | "team" | "disconnect" | null;
  status_age_days?: number | null;
  org_status_phase?: "pending" | "completed" | "none" | null;
  org_status_kind?: "formal" | "team" | "disconnect" | null;
  org_status_age_days?: number | null;
  response_roster_source?: "running" | "published" | null;
  response_roster_kind?: string | null;
  response_roster?: PortfolioRosterPerson[];
  report_assessment_id?: string | null;
}

export interface PortfolioAccountsResponse {
  total: number;
  accounts: PortfolioAccount[];
}

interface CustomerWorkspaceState {
  assignedAccounts: {
    items: AssignedAccount[];
    total: number;
    assigned_as_primary: number;
    assigned_as_secondary: number;
    assigned_as_executive: number;
    total_revenue: number;
    loading: boolean;
    error: string | null;
  };
  portfolioAccounts: {
    items: PortfolioAccount[];
    total: number;
    loading: boolean;
    error: string | null;
  };
  orgPortfolioAccounts: {
    items: PortfolioAccount[];
    total: number;
    loading: boolean;
    error: string | null;
  };
}

const initialState: CustomerWorkspaceState = {
  assignedAccounts: {
    items: [],
    total: 0,
    assigned_as_primary: 0,
    assigned_as_secondary: 0,
    assigned_as_executive: 0,
    total_revenue: 0,
    loading: false,
    error: null,
  },
  portfolioAccounts: {
    items: [],
    total: 0,
    loading: false,
    error: null,
  },
  orgPortfolioAccounts: {
    items: [],
    total: 0,
    loading: false,
    error: null,
  },
};

export const fetchPortfolioAccounts = createAsyncThunk(
  "customerWorkspace/fetchPortfolioAccounts",
  async (scope: "mine" | "org" = "mine", { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.getPortfolioAccounts(scope === "org" ? { scope: "org" } : {})
      );
      const content = (response as { content?: PortfolioAccountsResponse })
        ?.content;
      const payload = content ?? (response as PortfolioAccountsResponse);
      return {
        total: payload.total ?? payload.accounts?.length ?? 0,
        accounts: payload.accounts ?? [],
        scope,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch portfolio accounts";
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchAssignedAccounts = createAsyncThunk(
  "customerWorkspace/fetchAssignedAccounts",
  async (params: FetchAssignedAccountsParams = {}, { rejectWithValue }) => {
    try {
      const queryParams: Record<string, string> = {};
      if (params.tier) queryParams.tier = params.tier;
      if (params.region) queryParams.region = params.region;
      if (params.customer_level)
        queryParams.customer_level = params.customer_level;
      if (params.role) queryParams.role = params.role;

      if (params.sort_by) {
        queryParams.sort_by = `${params.sort_by}`;
      }

      const response = await get(API_URLS.getAssignedAccounts(queryParams));
      return response as AssignedAccountsResponse;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch assigned accounts";
      return rejectWithValue(errorMessage);
    }
  }
);

const customerWorkspaceSlice = createSlice({
  name: "customerWorkspace",
  initialState,
  reducers: {
    clearAssignedAccounts: state => {
      state.assignedAccounts = {
        items: [],
        total: 0,
        assigned_as_primary: 0,
        assigned_as_secondary: 0,
        assigned_as_executive: 0,
        total_revenue: 0,
        loading: false,
        error: null,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAssignedAccounts.pending, state => {
        state.assignedAccounts.loading = true;
        state.assignedAccounts.error = null;
      })
      .addCase(fetchAssignedAccounts.fulfilled, (state, action) => {
        state.assignedAccounts.loading = false;
        state.assignedAccounts.items = action.payload.accounts || [];
        state.assignedAccounts.total = action.payload.total || 0;
        state.assignedAccounts.assigned_as_primary =
          action.payload.assigned_as_primary || 0;
        state.assignedAccounts.assigned_as_secondary =
          action.payload.assigned_as_secondary || 0;
        state.assignedAccounts.assigned_as_executive =
          action.payload.assigned_as_executive || 0;
        state.assignedAccounts.total_revenue =
          action.payload.total_revenue || 0;
        state.assignedAccounts.error = null;
      })
      .addCase(fetchAssignedAccounts.rejected, (state, action) => {
        state.assignedAccounts.loading = false;
        state.assignedAccounts.items = [];
        state.assignedAccounts.total = 0;
        state.assignedAccounts.assigned_as_primary = 0;
        state.assignedAccounts.assigned_as_secondary = 0;
        state.assignedAccounts.assigned_as_executive = 0;
        state.assignedAccounts.total_revenue = 0;
        state.assignedAccounts.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch assigned accounts";
      });

    builder
      .addCase(fetchPortfolioAccounts.pending, (state, action) => {
        const bucket =
          action.meta.arg === "org"
            ? state.orgPortfolioAccounts
            : state.portfolioAccounts;
        bucket.loading = true;
        bucket.error = null;
      })
      .addCase(fetchPortfolioAccounts.fulfilled, (state, action) => {
        const bucket =
          action.payload.scope === "org"
            ? state.orgPortfolioAccounts
            : state.portfolioAccounts;
        bucket.loading = false;
        bucket.items = action.payload.accounts || [];
        bucket.total = action.payload.total || 0;
        bucket.error = null;
      })
      .addCase(fetchPortfolioAccounts.rejected, (state, action) => {
        const bucket =
          action.meta.arg === "org"
            ? state.orgPortfolioAccounts
            : state.portfolioAccounts;
        bucket.loading = false;
        bucket.items = [];
        bucket.total = 0;
        bucket.error =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch portfolio accounts";
      });
  },
});

export const { clearAssignedAccounts } = customerWorkspaceSlice.actions;
export default customerWorkspaceSlice.reducer;
